/**
 * Metro 2 Collection Review Panel
 * 
 * Specialized review for collection accounts:
 * - Collection Payment Status Review
 * - Collection Payment-History Profile Review
 * - Collection Original Creditor Missing
 * - Collection Completeness Review
 * - Debt Buyer / Original Creditor Role Conflict
 * - Chain of Title Verification Needed
 * - Duplicate Collection / Ownership Conflict
 * - Prior Collector Balance Conflict
 * - DOFD / Re-Aging Review
 */

import React from 'react';
import {
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Building2,
  DollarSign,
  Calendar,
  FileText,
  Link,
  Copy,
  Info,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';

// Collection issue configurations
const COLLECTION_ISSUES = {
  collection_payment_status_review: {
    label: 'Collection Payment Status Review',
    icon: AlertCircle,
    severity: 'high',
    description: 'Collection account shows Late 120 Days payment status, which may conflict with collection reporting standards.',
  },
  collection_payment_history_profile_conflict: {
    label: 'Collection Payment-History Profile Conflict',
    icon: AlertTriangle,
    severity: 'high',
    description: 'Collection account shows 30/60/90/120 late history which is unusual for collection accounts.',
  },
  collection_reporting_chargeoff_profile_conflict: {
    label: 'Collection Reporting as Charge-Off Profile Conflict',
    icon: AlertTriangle,
    severity: 'high',
    description: 'Collection account payment history shows CO/CO/CO pattern, indicating possible reporting confusion.',
  },
  current_status_monthly_history_conflict: {
    label: 'Current Status / Monthly History Conflict',
    icon: AlertTriangle,
    severity: 'high',
    description: 'Collection shows OK in monthly history while status is derogatory.',
  },
  collection_original_creditor_missing: {
    label: 'Collection Original Creditor Missing',
    icon: FileText,
    severity: 'medium',
    description: 'Original creditor information is missing, generic, unknown, or inconsistent.',
  },
  collection_completeness_review: {
    label: 'Collection Completeness Review',
    icon: Info,
    severity: 'medium',
    description: 'Collection account may be missing required Metro 2 fields for proper reporting.',
  },
  debt_buyer_original_creditor_role_conflict: {
    label: 'Debt Buyer / Original Creditor Role Conflict',
    icon: Building2,
    severity: 'high',
    description: 'Debt buyer and original creditor information conflicts or indicates improper role reporting.',
  },
  chain_of_title_verification_needed: {
    label: 'Chain of Title Verification Needed',
    icon: Link,
    severity: 'medium',
    description: 'Debt ownership chain needs verification to confirm proper collection authority.',
  },
  duplicate_collection_ownership_conflict: {
    label: 'Duplicate Collection / Ownership Conflict',
    icon: Copy,
    severity: 'high',
    description: 'Multiple collectors or duplicate accounts reporting on the same debt.',
  },
  prior_collector_balance_conflict: {
    label: 'Prior Collector Balance Conflict',
    icon: DollarSign,
    severity: 'high',
    description: 'Prior collector still reports balance after debt was sold or transferred.',
  },
  dofd_reaging_review: {
    label: 'DOFD / Re-Aging Review',
    icon: Calendar,
    severity: 'high',
    description: 'Date of First Delinquency may be missing, inconsistent, or indicates potential re-aging.',
  },
};

// Severity badge colors
const SEVERITY_COLORS = {
  high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-700',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-700',
  low: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-700',
};

// Collection Issue Item
const CollectionIssueItem = ({ issueKey, details }) => {
  const config = COLLECTION_ISSUES[issueKey];
  if (!config) return null;

  const IconComponent = config.icon;

  return (
    <div className={`p-3 rounded-lg border ${
      config.severity === 'high' 
        ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-700' 
        : 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700'
    }`}>
      <div className="flex items-start gap-3">
        <IconComponent className={`w-5 h-5 mt-0.5 ${
          config.severity === 'high' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'
        }`} />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`font-medium ${
              config.severity === 'high' ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'
            }`}>
              {config.label}
            </span>
            <Badge variant="outline" className={SEVERITY_COLORS[config.severity]}>
              {config.severity}
            </Badge>
          </div>
          <p className={`text-sm ${
            config.severity === 'high' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'
          }`}>
            {details || config.description}
          </p>
        </div>
      </div>
    </div>
  );
};

// Collection Account Details
const CollectionAccountDetails = ({ account }) => {
  const originalCreditor = account?.original_creditor || account?.originalCreditor;
  const collectorName = account?.creditor_name || account?.creditorName || account?.collector;
  const balance = account?.balance || 0;
  const openDate = account?.date_opened || account?.dateOpened;
  const dofd = account?.dofd || account?.date_of_first_delinquency;
  const lastPayment = account?.date_last_payment || account?.dateLastPayment;

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-stone-50 dark:bg-stone-800/50 rounded-lg">
      <div>
        <span className="text-xs text-stone-500 dark:text-stone-400 block">Collector</span>
        <span className="text-sm font-medium text-stone-800 dark:text-stone-200">{collectorName || 'N/A'}</span>
      </div>
      <div>
        <span className="text-xs text-stone-500 dark:text-stone-400 block">Original Creditor</span>
        <span className={`text-sm font-medium ${
          !originalCreditor || originalCreditor === 'Unknown' || originalCreditor === 'N/A'
            ? 'text-amber-600 dark:text-amber-400'
            : 'text-stone-800 dark:text-stone-200'
        }`}>
          {originalCreditor || 'Missing'}
        </span>
      </div>
      <div>
        <span className="text-xs text-stone-500 dark:text-stone-400 block">Balance</span>
        <span className="text-sm font-medium text-stone-800 dark:text-stone-200">
          ${(balance || 0).toLocaleString()}
        </span>
      </div>
      <div>
        <span className="text-xs text-stone-500 dark:text-stone-400 block">Collection Opened</span>
        <span className="text-sm font-medium text-stone-800 dark:text-stone-200">{openDate || 'N/A'}</span>
      </div>
      <div>
        <span className="text-xs text-stone-500 dark:text-stone-400 block">DOFD</span>
        <span className={`text-sm font-medium ${
          !dofd ? 'text-amber-600 dark:text-amber-400' : 'text-stone-800 dark:text-stone-200'
        }`}>
          {dofd || 'Missing'}
        </span>
      </div>
      <div>
        <span className="text-xs text-stone-500 dark:text-stone-400 block">Last Payment</span>
        <span className="text-sm font-medium text-stone-800 dark:text-stone-200">{lastPayment || 'N/A'}</span>
      </div>
    </div>
  );
};

// Main Metro 2 Collection Review Panel
const Metro2CollectionReviewPanel = ({
  account,
  detectedIssues = [],
  paymentHistoryIssues = [],
  balanceIssues = [],
  ownershipIssues = [],
  datesIssues = [],
  showAccountDetails = true,
}) => {
  // Combine all issues
  const allIssues = [
    ...detectedIssues,
    ...paymentHistoryIssues.map(i => typeof i === 'string' ? i : i.key),
    ...balanceIssues.map(i => typeof i === 'string' ? i : i.key),
    ...ownershipIssues.map(i => typeof i === 'string' ? i : i.key),
    ...datesIssues.map(i => typeof i === 'string' ? i : i.key),
  ].filter((v, i, a) => a.indexOf(v) === i); // Dedupe

  const hasIssues = allIssues.length > 0;
  const highSeverityCount = allIssues.filter(i => COLLECTION_ISSUES[i]?.severity === 'high').length;

  return (
    <Card className="border-stone-200 dark:border-stone-700" data-testid="metro2-collection-review-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2 text-stone-800 dark:text-stone-200">
            <Building2 className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            Collection Account Review
          </CardTitle>
          <Badge 
            variant="outline" 
            className={hasIssues 
              ? highSeverityCount > 0
                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
              : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
            }
          >
            {hasIssues ? `${allIssues.length} Issue(s)` : 'Clean'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Collection Notice */}
        <Alert className="bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-700">
          <Building2 className="h-4 w-4 text-orange-600 dark:text-orange-400" />
          <AlertTitle className="text-orange-700 dark:text-orange-300">Collection Account Detected</AlertTitle>
          <AlertDescription className="text-orange-600 dark:text-orange-400">
            This account is reporting as a collection. Collection accounts require special Metro 2 review 
            because payment status, monthly history, original creditor, balance, and ownership fields 
            may require verification. Collection accounts should not be treated as clean only because 
            all bureaus show the same values.
          </AlertDescription>
        </Alert>

        {/* Account Details */}
        {showAccountDetails && account && (
          <CollectionAccountDetails account={account} />
        )}

        {/* Detected Issues */}
        {hasIssues ? (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-stone-700 dark:text-stone-300">
              Detected Collection Issues
            </h4>
            {allIssues.map((issueKey, idx) => (
              <CollectionIssueItem key={idx} issueKey={issueKey} />
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-2 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
            <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            <p className="text-sm text-emerald-700 dark:text-emerald-300">
              No collection-specific Metro 2 issues detected. Standard verification may still apply.
            </p>
          </div>
        )}

        {/* Metro 2 Review Note */}
        <div className="p-3 bg-stone-100 dark:bg-stone-800 rounded-lg">
          <p className="text-xs text-stone-600 dark:text-stone-400">
            <strong>Metro 2 Note:</strong> For normal collection accounts, the monthly payment-history 
            grid is usually cleaner when blank/no data/not reported. Collections with OK, 30, 60, 90, 
            120, or CO in the monthly grid require additional review.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default Metro2CollectionReviewPanel;
export { CollectionIssueItem, CollectionAccountDetails, COLLECTION_ISSUES };
